/**
 * Platform (TrustJet) fee helpers.
 *
 * The rate is stored on `organization.platform_fee_percent` as a percent
 * number (e.g. `1.5` means 1.5%, NOT a fraction). Null / 0 / bogus values
 * fall back to the historical default of 1.5% so every org keeps working
 * without a BE migration.
 */

export const DEFAULT_PLATFORM_FEE_PERCENT = 1.5;

// Returns the percent number (1.5) — always positive. Use for labels.
export const getPlatformFeePercent = (organization) => {
  const raw = organization?.platform_fee_percent;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return DEFAULT_PLATFORM_FEE_PERCENT;
};

// Returns the decimal fraction (0.015) — use for math (multiply by subtotal).
export const getPlatformFeeRate = (organization) =>
  getPlatformFeePercent(organization) / 100;

// Trims trailing zeros so 1.50 renders as "1.5" but 1.25 stays "1.25".
export const formatPlatformFeePercent = (percent) => {
  const n = Number(percent);
  if (!Number.isFinite(n)) return String(percent ?? '');
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
};

/**
 * Normalize a raw bracket list into a sortable / matchable shape.
 *
 * The BE-side `organization_platform_fee_brackets` table isn't guaranteed
 * to return rows in order (it's a separate table joined via ?include).
 * Callers here (and the resolver below) don't need to care — we sort by
 * min_amount ASC and expand the open-top sentinel (max_amount = 0/null)
 * to Infinity so a bracket like `{min: 100000, max: 0}` reads as "100k+".
 *
 * Rows with a non-positive fee_percent are dropped defensively — a
 * misconfigured 0% bracket would silently discount the whole quote.
 */
const normalizeBrackets = (brackets) => {
  if (!Array.isArray(brackets) || brackets.length === 0) return [];
  return brackets
    .filter((b) => Number.isFinite(Number(b?.fee_percent)) && Number(b.fee_percent) > 0)
    .map((b) => {
      const min = Number(b.min_amount ?? 0);
      const rawMax = b.max_amount == null ? 0 : Number(b.max_amount);
      // 0 (or null) is the "unbounded" sentinel per the BE model.
      const max = rawMax === 0 ? Infinity : rawMax;
      return {
        ...b,
        _min: Number.isFinite(min) ? min : 0,
        _max: max,
        _percent: Number(b.fee_percent),
      };
    })
    .sort((a, b) => a._min - b._min);
};

/**
 * Resolve the effective platform fee PERCENT for a given subtotal.
 *
 * subtotal is expected to be `operator_cost + broker_markup` (the same
 * base every consumer already computes fees against).
 *
 * Priority order:
 *   1. organization.platform_fee_brackets — half-open [min, max) match.
 *      max_amount = 0 (or null) means "unbounded above".
 *   2. If subtotal exceeds every bracket AND no open-top exists, reuse
 *      the highest-max bracket's percent (extrapolate up — safer than
 *      silently falling to the flat rate, which may be 0 by design for
 *      bracket-based orgs).
 *   3. organization.platform_fee_percent (flat) if positive.
 *   4. DEFAULT_PLATFORM_FEE_PERCENT (1.5).
 */
export const resolvePlatformFeePercent = ({ organization, subtotal }) => {
  const amount = Number(subtotal);
  const normalized = normalizeBrackets(organization?.platform_fee_brackets);

  if (normalized.length > 0 && Number.isFinite(amount)) {
    // Half-open match. subtotal == boundary lands in the higher tier.
    const match = normalized.find((b) => amount >= b._min && amount < b._max);
    if (match) return match._percent;

    // Above every bracket — pick the top one. If an open-top bracket
    // exists, its _max = Infinity makes it the top automatically. Else
    // the highest-max finite bracket wins, extrapolated to +Infinity.
    const top = normalized.reduce((best, b) => {
      if (b._max > best._max) return b;
      if (b._max === best._max && b._min > best._min) return b;
      return best;
    }, normalized[0]);
    if (amount >= top._min) return top._percent;
    // Falls through only if subtotal is below every bracket's floor —
    // implies a config gap at the bottom. Uses flat/default fallback.
  }
  return getPlatformFeePercent(organization);
};

// Decimal fraction of resolvePlatformFeePercent — use for math.
export const resolvePlatformFeeRate = (params) =>
  resolvePlatformFeePercent(params) / 100;

/**
 * Historical accuracy: reverse-derive the effective percent from a
 * persisted platform_fee dollar amount + the row's subtotal. Used by
 * PriceBreakdownDrawer so a saved quote keeps showing the rate it was
 * priced at, even after the org changes its brackets.
 */
export const getEffectivePercentFromAmount = (platformFeeAmount, subtotal) => {
  const fee = Number(platformFeeAmount);
  const base = Number(subtotal);
  if (!Number.isFinite(fee) || !Number.isFinite(base) || base <= 0) return null;
  return (fee / base) * 100;
};
